import { useEffect, useRef, useState, useLayoutEffect, useMemo } from 'react';
import {
  StyledBadge,
  StyledEventsSection,
  StyledEventsButton,
  StyledMobileHeader,
  StyledMenu,
  StyledContents,
  StyledTitle,
  StyledLink,
  StyledEventLinks,
} from './EventsButton.styles';
import { useMenuContext } from '../../contexts/MenuContext';
import useLinkProps from '@/components/Link/hooks/useLinkProps';
import EventLink from './EventLink';
import { hasEventUpdates, saveEventsSnapshot } from '../../utils/eventUpdates';

export const MENU_ID = 'EVENTS';

const EventsButton = ({
  events,
  title,
  link,
  onClick,
}: {
  events: Queries.HEADERFragment['headerElements']['events'];
  title: string | null | undefined;
  link: Queries.LINKFragment | null | undefined;
  onClick: () => void;
}) => {
  const { activeMenuItemId, setActiveMenuItemId, menuButtonsRef, dropdownContentsRef } =
    useMenuContext();
  const isActive = activeMenuItemId === MENU_ID;
  const eventItems = useMemo(() => Object.values(events?.value ?? []), [events?.value]);
  const eventCount = eventItems.length;
  const hasEvents = eventCount > 0;
  const { linkChildren, linkProps } = useLinkProps({
    action: link?.linkElements?.action.value,
    target: link?.linkElements?.target?.value,
    event: link?.linkElements?.event?.value,
  });
  const linkText = link?.linkElements?.text.value;

  const eventsButtonRef = useRef<HTMLButtonElement>(null);
  const eventsMenuRef = useRef<HTMLDivElement>(null);

  const [hasUpdates, setHasUpdates] = useState(false);
  // Prevent flash on SSR: without hydrated flag, server renders badge,
  // then client updates it after checking localStorage, causing visible flash
  const [hydrated, setHydrated] = useState(false);
  const showBadge = hydrated && eventCount > 0 && hasUpdates;

  // Check for event updates on mount and when events change
  useEffect(() => {
    setHasUpdates(hasEventUpdates(eventItems));
    setHydrated(true);
  }, [eventItems]);

  // Hide badge when events menu opens and save state
  useLayoutEffect(() => {
    if (isActive && hasUpdates) {
      setHasUpdates(false);
      saveEventsSnapshot(eventItems);
    }
  }, [isActive, hasUpdates, eventItems]);

  useEffect(() => {
    const button = eventsButtonRef.current;
    const menu = eventsMenuRef.current;
    const menuButtonsSet = menuButtonsRef?.current;
    const dropdownContentsSet = dropdownContentsRef?.current;

    if (button && menuButtonsSet) {
      menuButtonsSet.add(button);
    }
    if (menu && dropdownContentsSet) {
      dropdownContentsSet.add(menu);
    }

    return () => {
      if (button && menuButtonsSet) {
        menuButtonsSet.delete(button);
      }
      if (menu && dropdownContentsSet) {
        dropdownContentsSet.delete(menu);
      }
    };
  }, [menuButtonsRef, dropdownContentsRef]);

  return (
    <StyledEventsSection>
      <StyledEventsButton
        onClick={onClick}
        ref={eventsButtonRef}
        aria-label="Open events menu"
        aria-controls={MENU_ID}
        aria-expanded={isActive}
      >
        <img src="/assets/calendar.svg" alt="Calendar Icon" />
        {showBadge && <StyledBadge>{eventCount}</StyledBadge>}
      </StyledEventsButton>
      <StyledMenu isActive={isActive} ref={eventsMenuRef}>
        <StyledMobileHeader>
          <button
            onClick={() => setActiveMenuItemId(null)}
            aria-label="Close events menu"
            aria-controls={MENU_ID}
            aria-expanded={isActive}
          >
            <img src="/assets/close.svg" alt="Close Icon" />
          </button>
        </StyledMobileHeader>
        <StyledContents>
          {title && (
            <StyledTitle>
              <div>{title}</div>
            </StyledTitle>
          )}
          <StyledEventLinks>
            {hasEvents && eventItems.map((event) => <EventLink key={event.system.id} {...event} />)}
          </StyledEventLinks>
          {link && (
            <StyledLink {...linkProps}>
              {linkText}
              {linkChildren}
            </StyledLink>
          )}
        </StyledContents>
      </StyledMenu>
    </StyledEventsSection>
  );
};

export default EventsButton;
